/**
 * Shared free-shipping (+20) eligibility and progress logic.
 */
(function (global) {
  var ELIGIBLE_TAGS = ['free_shipping_elifible', 'free_shipping_eligible'];
  var WHEEL_PRIZE_MATCH = /wheel\s*prize|wheelprize|wheel_prize/i;
  var handleCache = Object.create(null);
  var pendingFetches = Object.create(null);

  function getConfig() {
    return global.__MM_FS_CONFIG__ || {};
  }

  function normalizeHandle(handle) {
    return String(handle || '')
      .trim()
      .toLowerCase();
  }

  function itemHandle(item) {
    if (!item) return '';
    var handle = normalizeHandle(item.handle || item.product_handle);
    if (handle) return handle;
    var match = String(item.url || '').match(/\/products\/([^/?#]+)/);
    return match ? normalizeHandle(match[1]) : '';
  }

  function tagsIncludeEligible(rawTags) {
    var tags = String(rawTags || '')
      .split(',')
      .map(function (tag) {
        return tag.trim().toLowerCase();
      });
    return ELIGIBLE_TAGS.some(function (eligibleTag) {
      return tags.indexOf(eligibleTag) !== -1;
    });
  }

  function discountTitleMatchesWheel(title) {
    return WHEEL_PRIZE_MATCH.test(String(title || ''));
  }

  function hasWheelPrizeDiscount(item) {
    if (!item) return false;
    if (item.line_level_discount_allocations && item.line_level_discount_allocations.length) {
      for (var i = 0; i < item.line_level_discount_allocations.length; i++) {
        var alloc = item.line_level_discount_allocations[i];
        var title = (alloc.discount_application && alloc.discount_application.title) || alloc.title || '';
        if (discountTitleMatchesWheel(title)) return true;
      }
    }
    if (item.discounts && item.discounts.length) {
      for (var j = 0; j < item.discounts.length; j++) {
        if (discountTitleMatchesWheel(item.discounts[j].title || item.discounts[j].code)) return true;
      }
    }
    return false;
  }

  function isGiftItem(item) {
    if (!item || !item.properties) return false;
    var p = item.properties;
    if (p._gift === 'true' || p._offerType === 'CART_TIER_GIFT' || p.mm_offer_type === 'CART_TIER_GIFT') {
      return true;
    }
    return false;
  }

  function isItemEligibleSync(item) {
    if (!item) return false;
    if (isGiftItem(item)) return false;
    if (hasWheelPrizeDiscount(item)) return false;

    if (item.tags) {
      return tagsIncludeEligible(item.tags);
    }

    var handle = itemHandle(item);
    if (!handle) return null;
    if (Object.prototype.hasOwnProperty.call(handleCache, handle)) {
      return handleCache[handle];
    }
    return null;
  }

  function fetchHandleEligibility(handle) {
    var key = normalizeHandle(handle);
    if (!key) return Promise.resolve(false);
    if (Object.prototype.hasOwnProperty.call(handleCache, key)) {
      return Promise.resolve(handleCache[key]);
    }
    if (pendingFetches[key]) return pendingFetches[key];

    pendingFetches[key] = fetch('/products/' + encodeURIComponent(key) + '.js')
      .then(function (response) {
        if (!response.ok) throw new Error('product fetch failed');
        return response.json();
      })
      .then(function (product) {
        var eligible = tagsIncludeEligible(product.tags);
        handleCache[key] = eligible;
        return eligible;
      })
      .catch(function () {
        handleCache[key] = false;
        return false;
      })
      .finally(function () {
        delete pendingFetches[key];
      });

    return pendingFetches[key];
  }

  function calculateEligibleTotalSync(cart) {
    var eligible = 0;
    var hasExcluded = false;
    var unknownHandles = [];

    (cart && cart.items ? cart.items : []).forEach(function (item) {
      var eligibleState = isItemEligibleSync(item);
      if (eligibleState === null) {
        unknownHandles.push(itemHandle(item));
        hasExcluded = true;
        return;
      }
      if (eligibleState) {
        eligible += item.final_line_price || 0;
      } else {
        hasExcluded = true;
      }
    });

    return {
      eligible: eligible,
      hasExcluded: hasExcluded,
      unknownHandles: unknownHandles.filter(Boolean),
    };
  }

  function calculateEligibleTotal(cart) {
    var sync = calculateEligibleTotalSync(cart);
    var uniqueUnknown = sync.unknownHandles.filter(function (handle, index, list) {
      return list.indexOf(handle) === index;
    });

    if (!uniqueUnknown.length) {
      return Promise.resolve({
        eligible: sync.eligible,
        hasExcluded: sync.hasExcluded,
      });
    }

    return Promise.all(uniqueUnknown.map(fetchHandleEligibility)).then(function () {
      var finalResult = calculateEligibleTotalSync(cart);
      return {
        eligible: finalResult.eligible,
        hasExcluded: finalResult.hasExcluded,
      };
    });
  }

  function buildProgressFromEligible(eligibleBase, hasExcluded, options) {
    options = options || {};
    var cfg = getConfig();
    var thresholdBase = parseInt(options.threshold != null ? options.threshold : cfg.threshold, 10) || 0;
    var taxEnabled = options.taxEnabled != null ? !!options.taxEnabled : !!cfg.taxEnabled;
    var taxRate = options.taxRate != null ? parseFloat(options.taxRate, 10) : parseFloat(cfg.taxRate, 10) || 1;
    var eligiblePreTax = parseInt(eligibleBase, 10) || 0;
    var eligibleCompare = taxEnabled ? Math.round(eligiblePreTax * taxRate) : eligiblePreTax;
    var remaining = Math.max(0, thresholdBase - eligibleCompare);
    var progress =
      thresholdBase > 0 ? Math.min(100, Math.round((eligibleCompare * 100) / thresholdBase)) : 0;

    return {
      eligible: eligiblePreTax,
      eligibleCompare: eligibleCompare,
      thresholdBase: thresholdBase,
      remaining: remaining,
      remainingBase: remaining,
      progress: progress,
      unlocked: remaining <= 0,
      hasExcluded: !!hasExcluded,
    };
  }

  function computeProgress(cart, options) {
    options = options || {};

    if (typeof options.eligible === 'number') {
      return Promise.resolve(
        buildProgressFromEligible(options.eligible, !!options.hasExcluded, options)
      );
    }

    if (!cart || !cart.items || !cart.items.length) {
      return Promise.resolve(buildProgressFromEligible(0, false, options));
    }

    return calculateEligibleTotal(cart).then(function (result) {
      return buildProgressFromEligible(result.eligible, result.hasExcluded, options);
    });
  }

  function seedCache(map) {
    if (!map || typeof map !== 'object') return;
    Object.keys(map).forEach(function (handle) {
      handleCache[normalizeHandle(handle)] = !!map[handle];
    });
  }

  function cartFromEvent(event) {
    if (!event || !event.detail) return null;
    if (event.detail.cart && event.detail.cart.items) return event.detail.cart;
    if (event.detail.items) return event.detail;
    return null;
  }

  function fetchCart() {
    return fetch('/cart.js').then(function (response) {
      return response.json();
    });
  }

  function refreshFromCart(cart, options) {
    if (cart && cart.items) {
      return computeProgress(cart, options);
    }
    return fetchCart().then(function (fresh) {
      return computeProgress(fresh, options);
    });
  }

  global.mmShippingEligibility = {
    ELIGIBLE_TAGS: ELIGIBLE_TAGS,
    getConfig: getConfig,
    seedCache: seedCache,
    cartFromEvent: cartFromEvent,
    fetchCart: fetchCart,
    refreshFromCart: refreshFromCart,
    computeProgress: computeProgress,
    buildProgressFromEligible: buildProgressFromEligible,
    calculateEligibleTotal: calculateEligibleTotal,
    calculateEligibleTotalSync: calculateEligibleTotalSync,
    isItemEligibleSync: isItemEligibleSync,
    hasWheelPrizeDiscount: hasWheelPrizeDiscount,
  };

  if (global.__MM_FS_SEED__) {
    seedCache(global.__MM_FS_SEED__);
    delete global.__MM_FS_SEED__;
  }
})(window);
